import { Box } from '@factorialco/deprecated-design-system'
import { ExpensesEntitiesExpensableResourceTypeEnum } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import TitleHeader from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/TitleHeader'
import AdditionalInformationWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/AdditionalInformationWidget'
import AttachedDocumentsWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/AttachedDocumentsWidget'
import BudgetAndProjectWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/BudgetAndProjectWidget'
import CostCentersWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/CostCentersWidget'
import ExpenseInformationWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/ExpenseInformationWidget'
import PerDiemDetailsWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/PerDiemDetailsWidget'
import PerDiemRatesWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/PerDiemRatesWidget'
import TripInformationWidget from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/widgets/TripInformationWidget'
import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { isOwnExpensable } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

interface ConfirmationScreenProps {
  expensable: Expensable
  customizationFields: CustomizationField[]
  editable: boolean
  children?: React.ReactNode
  resourceType: ExpensesEntitiesExpensableResourceTypeEnum
  onEdit: (scrollTo?: string | undefined) => void
  isNewPerDiem?: boolean
  showBudgetAndProject?: boolean
  canEditBudgetAndProject?: boolean
  showCostCenters?: boolean
  showDescription?: boolean
  showInternalReference?: boolean
}
const ConfirmationScreen = ({
  expensable,
  customizationFields,
  editable,
  children,
  resourceType,
  onEdit,
  isNewPerDiem = false,
  showBudgetAndProject,
  canEditBudgetAndProject,
  showCostCenters,
  showDescription,
  showInternalReference,
}: ConfirmationScreenProps) => {
  const { employee: currentEmployee } = useCurrentSession()
  const isDraft = expensable.status === 'draft'
  const isExpensableOwner = isOwnExpensable(expensable, currentEmployee)
  const canReview = expensable.currentUserApprovalStatus === 'pending'
  const filesEditable =
    editable && isExpensableOwner && (isDraft || expensable.status === 'changes_requested')
  const showSubmitterText = isDraft && !canReview && isExpensableOwner
  const showReviewerText = canReview && !isExpensableOwner

  const anyTitleHeader = showSubmitterText || showReviewerText

  return (
    <Box gap="s16" paddingY="s32">
      <Box paddingBottom={anyTitleHeader ? 's0' : 's16'}>
        <BasicDetails expensable={expensable} />
      </Box>
      {showSubmitterText && (
        <TitleHeader
          titleLabel={i18n.t('expenses_v2.confirmation_screen.title_submitter')}
          subtitleLabel={i18n.t('expenses_v2.confirmation_screen.subtitle_submitter')}
        />
      )}
      {showReviewerText && (
        <TitleHeader
          titleLabel={i18n.t('expenses_v2.confirmation_screen.title_reviewer')}
          subtitleLabel={i18n.t('expenses_v2.confirmation_screen.subtitle_reviewer')}
        />
      )}
      <ExpenseInformationWidget
        expensable={expensable}
        customizationFields={customizationFields}
        editable={editable}
        onEdit={() => onEdit('basicInformation')}
        isOwnExpensable={isExpensableOwner}
        resourceType={'perdiem'}
      />
      {!isNewPerDiem && (
        <>
          <TripInformationWidget
            expensable={expensable}
            resourceType={resourceType}
            editable={editable}
            onEdit={() => onEdit('tripInformation')}
          />

          <PerDiemRatesWidget
            expensable={expensable}
            onEdit={() => onEdit('perDiemRates')}
            editable={editable}
          />
        </>
      )}
      {isNewPerDiem && (
        <PerDiemDetailsWidget
          expensable={expensable}
          editable={editable}
          onEditRoutes={() => onEdit('tripInformation')}
          onEditDeductions={() => onEdit('companyPaid')}
        />
      )}
      <AdditionalInformationWidget
        expensable={expensable}
        onEdit={() => onEdit('additionalInformation')}
        editable={editable}
        showDescription={showDescription && editable}
        showInternalReference={showInternalReference && editable}
      />
      <AttachedDocumentsWidget
        expensable={expensable}
        onEdit={() => onEdit('files')}
        editable={filesEditable}
      />
      <BudgetAndProjectWidget
        expensable={expensable}
        onEdit={() => onEdit('budgetAndProject')}
        editable={editable && (canEditBudgetAndProject ?? true)}
        showWhenEmpty={showBudgetAndProject && editable && (canEditBudgetAndProject ?? true)}
      />
      <CostCentersWidget
        expensable={expensable}
        onEdit={() => onEdit('costCenters')}
        editable={editable}
        showWhenEmpty={showCostCenters && editable}
      />
      {children}
    </Box>
  )
}

export default ConfirmationScreen
